import React from 'react';
import { motion } from 'framer-motion';
import CallToAction from '../components/shared/CallToAction';

const ClientsPage = () => {
  const clientCategories = [
    {
      name: "Grandes chaînes",
      clients: [
        { name: "Thala", logo: "/images/clients/thala.png", description: "Distribution exclusive d'huiles d'olive et produits gourmets" },
        { name: "Huilerie Kiared", logo: "/images/clients/huilerie_kiared.png", description: "Importation et distribution d'huiles artisanales de haute qualité" },
        { name: "El Walima", logo: "/images/clients/el_walima.png", description: "Placement stratégique de produits du Moyen-Orient dans les grandes surfaces" },
        { name: "Azemmour", logo: "/images/clients/azemmour.png", description: "Développement de la présence des produits azemmour sur le marché canadien" },
        { name: "Benna", logo: "/images/clients/benna.png", description: "Distribution exclusive de produits spécialisés" },
        { name: "Huile d'Or", logo: "/images/clients/huile_dor.png", description: "Partenariat pour la distribution d'huiles d'olive premium au Québec et en Ontario" }
      ]
    },
    {
      name: "Épiceries indépendantes",
      clients: [
        { name: "Réseau d'épiceries indépendantes", image: "/images/epiceries.jpg", description: "Plus de 120 épiceries indépendantes à travers le Québec et l'Ontario" }
      ]
    }
  ];

  const caseStudies = [
    {
      title: "Expansion rapide pour Saveurs de France",
      description: "Comment nous avons aidé cette entreprise française à passer de 0 à 75 points de vente en seulement 18 mois.",
      results: [
        "75 points de vente en 18 mois",
        "Croissance des ventes de 200% en an 2",
        "Introduction réussie de 3 nouvelles gammes de produits"
      ],
      image: "/images/case-studies/case1.jpg"
    },
    {
      title: "Gestion de la conformité pour Bio-Délices",
      description: "Mise en conformité complète d'une gamme de produits biologiques avec les réglementations canadiennes.",
      results: [
        "100% de conformité avec les normes ACIA",
        "Processus d'importation optimisé réduisant les délais de 40%",
        "Placement dans 3 chaînes majeures de distribution"
      ],
      image: "/images/case-studies/case2.jpg"
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Clients</h1>
          <p className="text-xl max-w-2xl">
            Découvrez les partenaires qui nous font confiance pour développer leurs produits sur le marché canadien.
          </p>
        </div>
      </div>

      {/* Client Categories */}
      {clientCategories.map((category, index) => (
        <section key={index} className={`section ${index % 2 === 1 ? 'bg-gray-100' : ''}`}>
          <div className="container-custom">
            {/* Only show title for categories other than "Grandes chaînes" */}
            {category.name !== "Grandes chaînes" && (
              <h2 className="section-title">{category.name}</h2>
            )}
            
            {category.name === "Grandes chaînes" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.clients.map((client, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white p-8 rounded-lg shadow-md flex flex-col items-center"
                  >
                    <img src={client.logo} alt={client.name} className="h-20 w-auto object-contain mb-6" />
                    <h3 className="text-xl font-bold mb-3">{client.name}</h3>
                    <p className="text-gray-600 text-center">{client.description}</p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-2xl font-bold mb-4">{category.clients[0].name}</h3>
                  <p className="text-gray-600 mb-6">{category.clients[0].description}</p>
                  
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h4 className="font-bold text-xl mb-2">Points clés</h4>
                    <ul className="space-y-2">
                      {category.name === "Épiceries indépendantes" ? (
                        <>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Réseau diversifié couvrant des zones urbaines et rurales</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Relations de confiance établies depuis 5+ ans</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Placement privilégié pour les nouveaux produits</span>
                          </li>
                        </>
                      ) : (
                        <>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Couverture nationale via ces centrales d'achat</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Volumes importants et croissance soutenue</span>
                          </li>
                          <li className="flex items-start">
                            <svg className="h-5 w-5 text-primary mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                            </svg>
                            <span>Partenariats stratégiques pour l'expansion</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <img 
                    src={category.clients[0].image} 
                    alt={category.clients[0].name} 
                    className="rounded-lg shadow-lg w-full h-auto"
                  />
                </motion.div>
              </div>
            )}
          </div>
        </section>
      ))}

      <CallToAction />
    </div>
  );
};

export default ClientsPage; 